import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import { useAuthHeader } from "react-auth-kit";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloud, faCheckCircle, faExclamationCircle, faPrint, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import "react-quill/dist/quill.snow.css";
import "./notepad.css";

const backend = process.env.REACT_APP_BACKEND_API;

export default function Notepad() {
  const authHeader = useAuthHeader();
  const token = authHeader();
  const versao = process.env.REACT_APP_VERSAO;

  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [lastSavedTime, setLastSavedTime] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    async function fetchNotes() {
      try {
        const response = await axios.get(backend + "/notepads", { headers: { Authorization: `${token}` } });
        const loadedNotes = Array.isArray(response.data) ? response.data : [];
        setNotes(loadedNotes);
        if (loadedNotes.length > 0) setSelectedNoteId(loadedNotes[0]._id);
      } catch (error) {
        console.error("Erro ao carregar as notas:", error);
        setSaveStatus("error");
      } finally {
        setInitialLoading(false);
      }
    }
    fetchNotes();
  }, [token]);

  useEffect(() => {
    const selectedNote = notes.find((note) => note._id === selectedNoteId);
    if (!selectedNote) return;
    setTitle(selectedNote.title || "Nota sem título");
    setContent(selectedNote.content || "");
    isFirstRender.current = true;
    setSaveStatus("idle");
  }, [selectedNoteId, notes]);

  useEffect(() => {
    if (!selectedNoteId || initialLoading) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setSaveStatus("saving");
    const timeoutId = setTimeout(async () => {
      try {
        await axios.put(`${backend}/notepads/${selectedNoteId}`, { title, content }, {
          headers: { Authorization: `${token}` },
        });
        setNotes((current) => current.map((note) => note._id === selectedNoteId ? { ...note, title, content } : note));
        setSaveStatus("saved");
        const now = new Date();
        setLastSavedTime(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
      } catch (error) {
        console.error("Erro ao salvar a nota:", error);
        setSaveStatus("error");
      }
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [title, content, selectedNoteId, initialLoading, token]);

  const createNote = async () => {
    const noteTitle = window.prompt("Nome da nota:", "Nova nota");
    if (!noteTitle || !noteTitle.trim()) return;
    try {
      const response = await axios.post(backend + "/notepads", { title: noteTitle.trim(), content: "" }, {
        headers: { Authorization: `${token}` },
      });
      const note = response.data.result || response.data;
      setNotes((current) => [...current, note]);
      setSelectedNoteId(note._id);
    } catch (error) {
      setSaveStatus("error");
    }
  };

  const handleContentChange = (value) => setContent(value);

  const deleteNote = async (note) => {
    if (!window.confirm(`Apagar a nota “${note.title || "Nota sem título"}”?`)) return;
    try {
      await axios.delete(`${backend}/notepads/${note._id}`, {
        headers: { Authorization: `${token}` },
      });
      const remainingNotes = notes.filter((current) => current._id !== note._id);
      setNotes(remainingNotes);
      if (selectedNoteId === note._id) setSelectedNoteId(remainingNotes[0]?._id || null);
    } catch (error) {
      console.error("Erro ao apagar a nota:", error);
      setSaveStatus("error");
    }
  };

  const handlePrint = () => {
    const now = new Date();
    const dia = String(now.getDate()).padStart(2, "0");
    const mes = String(now.getMonth() + 1).padStart(2, "0");
    const ano = now.getFullYear();
    const horas = String(now.getHours()).padStart(2, "0");
    const minutos = String(now.getMinutes()).padStart(2, "0");
    const dataHora = `${dia}/${mes}/${ano} - ${horas}:${minutos}`;

    const printWindow = window.open("", "_blank", "width=850,height=900");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <title>Notas - ${dataHora}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              margin: 40px;
              color: #000000;
              background-color: #ffffff;
              line-height: 1.4;
              font-size: 14pt;
            }
            .print-header {
              font-size: 18pt;
              font-weight: 700;
              border-bottom: 2px solid #2c3e50;
              padding-bottom: 12px;
              margin-bottom: 25px;
              color: #1a1a1a;
            }
            .print-content {
              word-wrap: break-word;
            }
            .print-content h1, .print-content h2, .print-content h3 {
              color: #111111;
              margin-top: 1.2em;
              margin-bottom: 0.5em;
            }
            .print-content p {
              margin: 0;
              padding: 0;
            }
            .print-content ul, .print-content ol {
              margin: 0.5em 0;
              padding-left: 2em;
            }
            .print-content li {
              margin: 0;
              padding: 0;
            }
            .print-content blockquote {
              border-left: 4px solid #cccccc;
              margin: 1.5em 0;
              padding-left: 1em;
              color: #444444;
            }
            .print-content code, .print-content pre {
              font-family: monospace;
              background-color: #f4f4f4;
              padding: 2px 4px;
              border-radius: 4px;
            }
            @page {
              margin: 2cm;
            }
          </style>
        </head>
        <body>
          <div class="print-header">Notas - ${dataHora}</div>
          <div class="print-content">${content}</div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      alert("Por favor, permita pop-ups no seu navegador para abrir a página de impressão.");
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold", "italic", "underline", "strike",
    "color", "background",
    "list", "bullet",
    "align",
    "blockquote", "code-block"
  ];

  return (
    <Container fluid className="notepad-container">
      <Row className="notepad-layout g-2">
        <Col md={3} lg={2} className="notepad-sidebar">
          <div className="notepad-card notepad-notes-panel">
            <div className="notepad-sidebar-header">
              <span><FontAwesomeIcon icon={faCloud} className="me-2 text-success" />Minhas notas</span>
              <button type="button" className="notepad-new-button" onClick={createNote} title="Nova nota">+</button>
            </div>
            <button type="button" className="notepad-new-note" onClick={createNote}>+ Nova nota</button>
            <div className="notepad-note-list">
              {initialLoading && <span className="notepad-empty">Carregando...</span>}
              {!initialLoading && notes.length === 0 && <span className="notepad-empty">Crie sua primeira nota.</span>}
              {notes.map((note) => (
                <div key={note._id} className={`notepad-note-item ${note._id === selectedNoteId ? "active" : ""}`}>
                  <button type="button" className="notepad-note-select" onClick={() => setSelectedNoteId(note._id)}>
                    <strong>{note.title || "Nota sem título"}</strong>
                    <small>{(note.content || "").replace(/<[^>]+>/g, "").slice(0, 48) || "Sem conteúdo"}</small>
                  </button>
                  <button type="button" className="notepad-delete-button" onClick={() => deleteNote(note)} title="Apagar nota" aria-label={`Apagar ${note.title || "nota"}`}>
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Col>
        <Col md={9} lg={10} className="notepad-main">
          <div className="notepad-card">
            <div className="notepad-header d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <input className="notepad-title-input" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Título da nota" disabled={!selectedNoteId} />
                <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center print-btn shadow-sm" onClick={handlePrint} disabled={!selectedNoteId} title="Imprimir nota">
                  <FontAwesomeIcon icon={faPrint} className="me-2" />Imprimir
                </button>
              </div>
              <div className="notepad-status d-flex align-items-center">
                {saveStatus === "saving" && <span className="status-saving"><Spinner animation="border" size="sm" className="me-2" />Salvando...</span>}
                {saveStatus === "saved" && <span className="status-saved"><FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />Salvo {lastSavedTime && `às ${lastSavedTime}`}</span>}
                {saveStatus === "error" && <span className="status-error text-danger"><FontAwesomeIcon icon={faExclamationCircle} className="me-1" />Erro ao salvar</span>}
              </div>
            </div>
            <div className="notepad-editor-wrapper">
              {!selectedNoteId && !initialLoading ? <div className="notepad-loading d-flex align-items-center justify-content-center">Selecione ou crie uma nota.</div> : initialLoading ? <div className="notepad-loading d-flex flex-column align-items-center justify-content-center"><Spinner animation="border" variant="success" className="mb-2" />Carregando suas notas...</div> : <ReactQuill className="notepad-quill-editor" theme="snow" value={content} onChange={handleContentChange} modules={modules} formats={formats} placeholder="Escreva sua anotação aqui..." />}
            </div>
          </div>
          <footer><p className="footer-text text-center">Versão {versao} - &copy;2024 <a href="http://www.tbmed.com.br" className="no-link">TBMed Serviços Médicos</a> LTDA.</p></footer>
        </Col>
      </Row>
    </Container>
  );
}
